import PrimaryButton from "components/buttons/primary-button";
import Typography from "components/typography";
import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Colors } from "utils/colors";
import ArtistItem from "./artistItem";
import Modal from "components/modal";
import AddArtistModal from "components/modal/modals/addArtist";
import {
  ITrackArtists,
  ITrackContributor,
  ITrackWriter
} from "types/musicData";
import AddContributorModal from "components/modal/modals/addContributor";
import AddWriterModal from "components/modal/modals/addWriter";
import SavedCreditsModal from "../modals/savedCreditsModal";
import { useNavigate } from "react-router-dom";
import api from "services/axiosInstance";
import { ConvertApiResponseToMusicData } from "../../../edit-metadata/components/convertApiResponseToMusicData";
import Toast from "components/toast";
import Spinner from "components/spinner";

interface IProps {
  title: string;
  trackId: string;
}
const TrackCredits = ({ title, trackId }: IProps) => {
  const [artists, setArtists] = useState<ITrackArtists[]>([]);
  const [loading, setLoading] = useState(false);
  const [contributors, setContributors] = useState<ITrackContributor[]>([]);
  const [writers, setWriters] = useState<ITrackWriter[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeModal, setActiveModal] = useState("artist");
  const [activeEditIndex, setActiveEditIndex] = useState<number | undefined>();
  const [saveLoading, setSaveLoading] = useState(false);
  const navigate = useNavigate();

  const GetData = async () => {
    if (!trackId || loading) return;
    try {
      setLoading(true);

      const { data: artistsData } = await api.get(`/songs/${trackId}/artists`);

      const { data: contributorsData } = await api.get(
        `collaborators/${trackId}/collaborators`
      );

      const artists = artistsData.data;

      const collaboratorsData = contributorsData.data;

      setArtists([
        { ...artists.primaryArtist, type: "Primary Artist" },
        ...artists.featuredPlatformArtists,
        ...artists.featuredTempArtists
      ]);
      setContributors(collaboratorsData);
    } catch (error) {
      Toast({ error });
    } finally {
      setLoading(false);
    }
  };

  const OpenEdit = (
    index: number,
    activeModal: "artist" | "contributor" | "writer" | "save"
  ) => {
    setModalOpen(true);
    setActiveModal(activeModal);
    setActiveEditIndex(index);
  };

  //artist

  const handleEditArtist = (index: number, editedArtist: ITrackArtists) => {
    const updatedArtists = artists.map((artist, i) =>
      i === index ? { ...editedArtist } : artist
    );
    setArtists([...updatedArtists]);
    setActiveEditIndex(undefined);
  };
  const DeletArtist = (index: number) => {
    const filtered = artists.filter((artist, i) => i !== index);
    setArtists([...filtered]);

    api.delete(`/songs/${trackId}/artists/${artists[index].id}`);
  };
  const handleAddArtist = (artist: ITrackArtists) => {
    setArtists([...artists, artist]);

    if (artist.id) {
      api.post(`/songs/${trackId}/artists`, { artistId: artist.id });
    } else {
      api.post(`/songs/${trackId}/artists`, {
        tempArtistName: artist.name,
        ...artist
      });
    }
  };

  //contributor
  const handleAddContributor = (contributor: ITrackContributor) => {
    setContributors([...contributors, contributor]);

    api.post(`collaborators/${trackId}/collaborators`, contributor);
  };

  const handleEditContributor = (index: number, edited: ITrackContributor) => {
    const updated = contributors.map((artist, i) =>
      i === index ? { ...edited } : artist
    );
    setContributors([...updated]);
    setActiveEditIndex(undefined);
  };
  const DeletContributor = (index: number) => {
    const filtered = contributors.filter((c, i) => i !== index);
    setContributors([...filtered]);

    api.delete(
      `collaborators/${trackId}/collaborators/${contributors[index].id}`
    );
  };

  const SaveDisabled = useMemo(() => {
    if (artists.length > 0 || contributors.length > 0 || !saveLoading) {
      return false;
    }
    return true;
  }, [artists, contributors, saveLoading]);

  const OnSave = async () => {
    try {
      setSaveLoading(true);
      navigate(-1);
    } catch (error) {
      Toast({ error });
    } finally {
      setSaveLoading(false);
    }
  };

  useEffect(() => {
    GetData();
  }, []);

  if (loading)
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          marginTop: "5rem",
          width: "100%"
        }}
      >
        <Spinner />
      </div>
    );

  return (
    <Container>
      {/* artist */}
      <Typography size="3xl" weight="bold" mb="0.8" color={Colors.white100}>
        Track Artists
      </Typography>
      <Typography color={Colors.grey400}>
        Add artists for your track, including Primary Artist, Featured Artist,
        or Remixer.
      </Typography>
      {artists.map((artist, i) => {
        return (
          <ArtistItem
            key={i}
            name={artist.name}
            role={artist.type}
            editItem={() => OpenEdit(i, "artist")}
            deleteItem={() => DeletArtist(i)}
            type="Artist"
          />
        );
      })}

      <Btn>
        <PrimaryButton
          onClick={() => {
            setModalOpen(true);
            setActiveModal("artist");
          }}
          text="Add Artist(s)"
          variant="text"
          icon={
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="100%"
              height="100%"
              viewBox="0 0 14 14"
              fill="none"
            >
              <path
                d="M7.0013 12.8332C10.223 12.8332 12.8346 10.2215 12.8346 6.99984C12.8346 3.77818 10.223 1.1665 7.0013 1.1665C3.77964 1.1665 1.16797 3.77818 1.16797 6.99984C1.16797 10.2215 3.77964 12.8332 7.0013 12.8332Z"
                stroke="#FEB73D"
                stroke-width="0.875"
              />
              <path
                d="M8.75 7H7M7 7H5.25M7 7V5.25M7 7V8.75"
                stroke="#FEB73D"
                stroke-width="0.875"
                stroke-linecap="round"
              />
            </svg>
          }
          iconPosition="left"
          iconSize="1.4"
        />
      </Btn>

      {/* writer */}
      {/* <Typography
        size="3xl"
        weight="bold"
        mb="0.8"
        mt="4"
        color={Colors.white100}
      >
        Songwriter
      </Typography>
      <Typography color={Colors.grey400}>
        Add the key creators behind your track, including the Composer and
        Lyricist.
      </Typography>
      {writers.map((writer, i) => {
        return (
          <ArtistItem
            key={i}
            name={`${writer.firstName} ${writer.lastName}`}
            editItem={() => OpenEdit(0, "writer")}
            deleteItem={() => DeletArtist(0)}
            type="Writer"
          />
        );
      })}

      <Btn>
        <PrimaryButton
          text="Add Writer(s)"
          variant="text"
          onClick={() => {
            setModalOpen(true);
            setActiveModal("writer");
          }}
          icon={
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="100%"
              height="100%"
              viewBox="0 0 14 14"
              fill="none"
            >
              <path
                d="M7.0013 12.8332C10.223 12.8332 12.8346 10.2215 12.8346 6.99984C12.8346 3.77818 10.223 1.1665 7.0013 1.1665C3.77964 1.1665 1.16797 3.77818 1.16797 6.99984C1.16797 10.2215 3.77964 12.8332 7.0013 12.8332Z"
                stroke="#FEB73D"
                stroke-width="0.875"
              />
              <path
                d="M8.75 7H7M7 7H5.25M7 7V5.25M7 7V8.75"
                stroke="#FEB73D"
                stroke-width="0.875"
                stroke-linecap="round"
              />
            </svg>
          }
          iconPosition="left"
          iconSize="1.4"
        />
      </Btn> */}
      {/* contributor */}
      <Typography
        size="3xl"
        weight="bold"
        mb="0.8"
        mt="4"
        color={Colors.white100}
      >
        Contributor
      </Typography>
      <Typography color={Colors.grey400}>
        Add additional contributors to your track, such as the Producer and Mix
        Engineer.
      </Typography>

      {contributors.map((c, i) => {
        return (
          <ArtistItem
            key={i}
            name={c.name}
            role={c.role}
            editItem={() => OpenEdit(i, "contributor")}
            deleteItem={() => DeletContributor(i)}
            type="Contributor"
          />
        );
      })}
      <Btn>
        <PrimaryButton
          onClick={() => {
            setModalOpen(true);
            setActiveModal("contributor");
          }}
          text="Add Contributor(s)"
          variant="text"
          icon={
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="100%"
              height="100%"
              viewBox="0 0 14 14"
              fill="none"
            >
              <path
                d="M7.0013 12.8332C10.223 12.8332 12.8346 10.2215 12.8346 6.99984C12.8346 3.77818 10.223 1.1665 7.0013 1.1665C3.77964 1.1665 1.16797 3.77818 1.16797 6.99984C1.16797 10.2215 3.77964 12.8332 7.0013 12.8332Z"
                stroke="#FEB73D"
                stroke-width="0.875"
              />
              <path
                d="M8.75 7H7M7 7H5.25M7 7V5.25M7 7V8.75"
                stroke="#FEB73D"
                stroke-width="0.875"
                stroke-linecap="round"
              />
            </svg>
          }
          iconPosition="left"
          iconSize="1.4"
        />
      </Btn>
      <CreditsFooter>
        <SaveButton>
          <PrimaryButton
            text={saveLoading ? "Saving..." : "Save Credits"}
            disabled={SaveDisabled}
            onClick={OnSave}
          />
        </SaveButton>
      </CreditsFooter>

      {/* Modal */}
      <Modal isActive={modalOpen} closeModal={() => setModalOpen(false)}>
        {activeModal === "artist" ? (
          <AddArtistModal
            close={() => setModalOpen(false)}
            confirm={(artist) => {
              handleAddArtist(artist);
            }}
            artistData={
              typeof activeEditIndex === "number"
                ? artists[activeEditIndex]
                : undefined
            }
            confirmEdit={(artist) => {
              if (typeof activeEditIndex === "number") {
                handleEditArtist(activeEditIndex, artist);
              }
            }}
            excludeArtistIds={artists.map((artist) => artist.id)}
          />
        ) : activeModal === "contributor" ? (
          <AddContributorModal
            open={activeModal === "contributor"}
            close={() => setModalOpen(false)}
            trackId={trackId}
            confirm={(contributor) => {
              handleAddContributor(contributor);
            }}
            confirmEdit={(c) => {
              if (typeof activeEditIndex === "number") {
                handleEditContributor(activeEditIndex, c);
              }
            }}
            editData={
              typeof activeEditIndex === "number"
                ? contributors[activeEditIndex]
                : undefined
            }
          />
        ) : activeModal === "save" ? (
          <SavedCreditsModal
            close={() => navigate("/overview")}
            title={title}
          />
        ) : (
          <></>
        )}
      </Modal>
    </Container>
  );
};

export default TrackCredits;

const Container = styled.div`
  margin-top: 5rem;
`;

const Btn = styled.div`
  width: fit-content;
  margin-top: 3.2rem;
`;

const CreditsFooter = styled.div`
  position: fixed;
  bottom: 0;
  width: 75%;
  border-top: 1px solid ${Colors.grey700};
  display: flex;
  padding: 1.6rem 0;
  align-items: center;
  @media only screen and (max-width: 769px) {
    width: 90%;
  }
`;
const SaveButton = styled.div`
  width: fit-content;
  margin-left: auto;
  @media only screen and (max-width: 769px) {
    width: 100%;
    margin-left: unset;
  }
`;
